import {CloseNewIcon} from '../../asset/image'
import {selectTheme} from '../../store'
import {ButtonTemplate, ButtonSize, ButtonColorTheme} from '..'

export enum PopupSize {
  Xl = 'xl',
  Large = 'large',
  Big = 'big',
  Medium = 'medium',
  Small = 'small',
  Tall = 'tall',
}

export const PopupTemplate = (props: {
  title: React.ReactNode | string
  description?: React.ReactNode | string
  background: boolean
  popupSize: PopupSize
  handleClosePopup: () => void
  mainFunction?: (arg?: any) => void
  buttonContent?: React.ReactNode | string
  children?: React.ReactNode | string
  buttonColorTheme?: ButtonColorTheme
  buttonDisabled?: boolean
  bannerImgUrl?: string
  optionalBottomLeftContent?: React.ReactNode
  titleIconURL?: string
  hideCancelButton?: boolean
  zIndex?: number
  hideBottomPart?: boolean
  centerPartBackgroundColor?: string
  optionalSecondaryButtonContent?: React.ReactNode | string
  optionalSecondaryButtonFunction?: (arg?: any) => void
  optionalSecondaryButtonColorTheme?: ButtonColorTheme
  optionalSecondaryButtonDisabled?: boolean
}) => {
  const {color, fontSize, fontWeight, borderRadius} = selectTheme()
  const {
    title,
    description,
    background,
    popupSize,
    handleClosePopup,
    mainFunction,
    buttonContent,
    children,
    buttonColorTheme,
    buttonDisabled,
    bannerImgUrl,
    optionalBottomLeftContent,
    titleIconURL,
    hideCancelButton,
    zIndex,
    hideBottomPart,
    centerPartBackgroundColor,
    optionalSecondaryButtonContent,
    optionalSecondaryButtonFunction,
    optionalSecondaryButtonColorTheme,
    optionalSecondaryButtonDisabled,
  } = props

  const popupSizeMapping = {
    [PopupSize.Xl]: {
      width: '960px',
      minHeight: '480px',
    },
    [PopupSize.Large]: {
      width: '960px',
      minHeight: '177px',
    },
    [PopupSize.Big]: {
      width: '880px',
      minHeight: '480px',
    },
    [PopupSize.Medium]: {
      width: '600px',
      minHeight: bannerImgUrl ? '444px' : '320px',
    },
    [PopupSize.Small]: {
      width: '480px',
      minHeight: '240px',
    },
    [PopupSize.Tall]: {
      width: '480px',
      minHeight: '520px',
    },
  }

  return (
    <div
      css={{
        position: 'fixed',
        zIndex: zIndex ?? 104,
        width: '100vw',
        height: '100vh',
        top: 0,
        left: 0,
        backgroundColor: background ? color.popupBackground : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...popupSizeMapping[popupSize],
          maxHeight: '90vh',
          borderRadius: borderRadius.popup,
          backgroundColor: color.surface.white,
          overflow: 'auto',
          position: 'relative',
        }}>
        {/* banner image */}
        {bannerImgUrl && (
          <div
            css={{
              width: '100%',
              height: '248px',
              backgroundImage: `url(${bannerImgUrl})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}
        {/* top part: title + description */}
        <div
          css={{
            padding: '24px 24px 8px 24px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            position: 'sticky',
            top: '0',
            backgroundColor: color.surface.white,
            zIndex: 9,
          }}>
          {/* title */}
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
              {titleIconURL && (
                <img
                  src={titleIconURL}
                  css={{
                    width: '24px',
                    height: '24px',
                  }}
                />
              )}
              <span
                css={{
                  fontSize: fontSize.h3,
                  fontWeight: fontWeight.thick,
                }}>
                {title}
              </span>
            </div>
            {!bannerImgUrl && (
              <button
                onClick={handleClosePopup}
                css={{
                  width: '24px',
                  minWidth: '24px',
                  height: '24px',
                  minHeight: '24px',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: 'transparent',
                  backgroundImage: `url(${CloseNewIcon})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  ':hover': {
                    backgroundColor: color.surface.default,
                  },
                }}
              />
            )}
          </div>

          {/* description */}
          {typeof description === 'string' && description.length > 0 && (
            <p
              css={{
                color: color.textIcon.secondary,
                fontWeight: fontWeight.bold,
              }}>
              {description}
            </p>
          )}
          {typeof description !== 'string' && description}
        </div>

        {/* center part: content */}
        {children && (
          <div
            css={{
              padding: '32px 24px',
              flex: '1',
              backgroundColor: centerPartBackgroundColor ?? color.surface.white,
            }}>
            {children}
          </div>
        )}
        {/* bottom part: cancel button + main button + (extra button)*/}
        {!hideBottomPart && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderTop: `1px solid ${color.border.light}`,
              position: 'sticky',
              bottom: '0',
              backgroundColor: color.surface.white,
              zIndex: 9,
              // boxShadow: '0px -2px 24px 0px rgba(0, 0, 0, 0.08)',
            }}>
            <div>{optionalBottomLeftContent}</div>
            <div
              css={{
                display: 'inline-flex',
                gap: '8px',
              }}>
              {/* cancel button */}
              {!hideCancelButton && (
                <ButtonTemplate
                  {...{
                    buttonSize: ButtonSize.Big,
                    onClick: handleClosePopup,
                    colorTheme: ButtonColorTheme.Grey,
                  }}>
                  Cancel
                </ButtonTemplate>
              )}

              {/* main button */}
              <div
                css={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}>
                <ButtonTemplate
                  {...{
                    buttonSize: ButtonSize.Big,
                    onClick:
                      mainFunction ??
                      (() => {
                        return
                      }),
                    colorTheme: buttonColorTheme,
                    disabled: buttonDisabled,
                  }}>
                  {buttonContent ?? ''}
                </ButtonTemplate>
                {optionalSecondaryButtonContent && (
                  <ButtonTemplate
                    {...{
                      buttonSize: ButtonSize.Big,
                      onClick:
                        optionalSecondaryButtonFunction ??
                        (() => {
                          return
                        }),
                      colorTheme: optionalSecondaryButtonColorTheme ?? ButtonColorTheme.Grey,
                      disabled: optionalSecondaryButtonDisabled,
                    }}>
                    {optionalSecondaryButtonContent}
                  </ButtonTemplate>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
